import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../store/auth";

export default function Welcome() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center px-4">
      {/* Glow ring */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-violet-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-lg w-full text-center space-y-8">
        {/* Icon */}
        <div className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-violet-600/20 border border-violet-500/30">
          <CheckCircleIcon className="w-10 h-10 text-violet-400" />
        </div>

        {/* Heading */}
        <div className="space-y-3">
          <p className="text-sm font-medium text-violet-400 tracking-widest uppercase">
            {t("welcome.eyebrow")}
          </p>
          <h1 className="text-4xl font-bold text-white">
            {user?.name ? t("welcome.titleName", { name: user.name.split(" ")[0] }) : t("welcome.title")}
          </h1>
          <p className="text-neutral-400 text-lg leading-relaxed">
            {t("welcome.subtitle")}
          </p>
        </div>

        {/* Steps */}
        <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 text-left space-y-4">
          <p className="text-xs font-semibold text-neutral-500 uppercase tracking-widest">
            {t("welcome.nextSteps")}
          </p>
          {(["step1", "step2", "step3"] as const).map((key, i) => (
            <div key={key} className="flex items-start gap-3">
              <span className="mt-0.5 flex-shrink-0 w-6 h-6 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">
                {i + 1}
              </span>
              <div>
                <p className="text-white text-sm font-medium">
                  {t(`welcome.steps.${key}.title`)}
                </p>
                <p className="text-neutral-400 text-sm">
                  {t(`welcome.steps.${key}.desc`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <Link
          to="/instagram-accounts/new"
          className="block w-full rounded-xl bg-violet-600 hover:bg-violet-500 text-white font-semibold py-3 px-6 transition-colors"
        >
          {t("welcome.cta")}
        </Link>
        <Link
          to="/dashboard"
          className="block text-sm text-neutral-500 hover:text-neutral-300 transition-colors"
        >
          {t("welcome.skip")}
        </Link>
      </div>
    </div>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
    </svg>
  );
}
